import { useCallback, useEffect, useState } from 'react';
import type { RefObject } from 'react';

const getFullscreenElement = (): Element | null => {
  if (document.fullscreenElement) return document.fullscreenElement;
  const doc = document as Document & {
    webkitFullscreenElement?: Element | null;
  };
  return doc.webkitFullscreenElement ?? null;
};

const requestFullscreenOn = async (el: HTMLElement): Promise<void> => {
  if (typeof el.requestFullscreen === 'function') {
    await el.requestFullscreen();
    return;
  }
  const wk = el as HTMLElement & {
    webkitRequestFullscreen?: () => void;
  };
  if (typeof wk.webkitRequestFullscreen === 'function') {
    wk.webkitRequestFullscreen();
    return;
  }
  throw new Error('Fullscreen no soportado');
};

const exitFullscreenDocument = async (): Promise<void> => {
  if (typeof document.exitFullscreen === 'function') {
    await document.exitFullscreen();
    return;
  }
  const doc = document as Document & {
    webkitExitFullscreen?: () => void;
  };
  if (typeof doc.webkitExitFullscreen === 'function') {
    doc.webkitExitFullscreen();
    return;
  }
  throw new Error('Salir de pantalla completa no soportado');
};

const FULLSCREEN_ERROR_MESSAGE =
  'No se pudo cambiar el modo pantalla completa. Probá con otro navegador o revisá los permisos del sitio.';

interface UseElementFullscreenOptions {
  targetRef: RefObject<HTMLElement | null>;
  onError?: (message: string) => void;
}

export const useElementFullscreen = ({
  targetRef,
  onError,
}: UseElementFullscreenOptions) => {
  const [isFullscreen, setIsFullscreen] = useState(false);

  const syncFullscreenState = useCallback(() => {
    const target = targetRef.current;
    const active = target != null && getFullscreenElement() === target;
    setIsFullscreen(active);
  }, [targetRef]);

  useEffect(() => {
    syncFullscreenState();
    document.addEventListener('fullscreenchange', syncFullscreenState);
    document.addEventListener(
      'webkitfullscreenchange' as keyof DocumentEventMap,
      syncFullscreenState
    );
    return () => {
      document.removeEventListener('fullscreenchange', syncFullscreenState);
      document.removeEventListener(
        'webkitfullscreenchange' as keyof DocumentEventMap,
        syncFullscreenState
      );
    };
  }, [syncFullscreenState]);

  const toggleFullscreen = useCallback(async () => {
    const el = targetRef.current;
    if (!el) return;

    try {
      if (getFullscreenElement() === el) {
        await exitFullscreenDocument();
        return;
      }
      await requestFullscreenOn(el);
    } catch (error) {
      console.error('Pantalla completa:', error);
      onError?.(FULLSCREEN_ERROR_MESSAGE);
    }
  }, [targetRef, onError]);

  return { isFullscreen, toggleFullscreen };
};
